import React, {useRef} from 'react';
import {
  ActivityIndicator,
  Animated,
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import {useButtonLayout} from '@/shared/ui/button-layout';
import {fonts} from '@/shared/ui/fonts';
import {Icon, type IconName} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {controls, radii, useTheme, type Palette} from '@/shared/ui/theme';

/**
 * Кнопки — `Button` дизайн-системы SamVision (`ui-kit/components/button`), вариант
 * за вариантом.
 *
 * ## Клавиша пульта, а не наклейка
 *
 * Кнопка рабочей станции прямоугольная (радиус 2pt), с видимой рамкой и без теней.
 * Главная — залита фирменным красным, и такая на экране ОДНА: «Сохранить», «Войти»,
 * «Открыть». Остальные — с рамкой или без, серые, и красным отвечают только на
 * наведение. Так красное остаётся сигналом, а не фоном.
 *
 * ## The tones
 *
 *   primary      — красная заливка. Действие, ради которого экран существует.
 *   destructive  — ui-kit `danger`: мягкая заливка опасности, на наведение — плотная.
 *                  Коралловый, не фирменный красный: удаление отличается от «главного».
 *   outline      — ui-kit `secondary`: рамка `border-strong` на прозрачном, серая в
 *                  покое, красная под мышью. Кнопка по умолчанию рядом с главной.
 *   secondary    — тихая заливка (`surface-overlay`) для глагола, который не главный и
 *                  не опасный. `soft` — то же самое под привычным именем.
 *   ghost        — без поверхности: третьестепенное действие внутри панели.
 *   toolbar      — плоская и без рамки, для полосы глаголов над списком.
 *   chrome       — для оправы: панель, полоса, вкладки. Оправа — другая поверхность, и
 *                  её контролы красятся против НЕЁ.
 *
 * ## Sizes, and the 44pt floor
 *
 * xs/sm/md — это sm/md/lg рабочей станции (24/28/34). `lg` — 44: пол касания, и
 * его берёт то, во что на телефоне попадают пальцем. Every smaller button carries
 * `hitSlop` sized to bring its TOUCH target to 44pt even when its painted box is 24.
 *
 * Подпись главной — полужирная, остальных — обычная: так у ui-kit, и так главная
 * узнаётся и без цвета.
 *
 * Titles are passed in; nothing user-facing is written here.
 */
export type ButtonTone =
  | 'primary'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'soft'
  | 'ghost'
  | 'toolbar'
  | 'chrome'
  /** @deprecated Use `primary`. `signal` is the live/online status colour. */
  | 'signal'
  /** @deprecated Use `outline`. */
  | 'neutral';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

type Skin = {
  bg: string;
  fg: string;
  border: string;
  /** Что меняется под курсором или пальцем. */
  hot: {bg: string; fg: string; border: string};
  /** Полужирная подпись — у главной и у опасной. */
  strong?: boolean;
};

function skin(p: Palette, tone: ButtonTone): Skin {
  switch (tone) {
    case 'primary':
    case 'signal':
      return {
        bg: p.primary,
        fg: p.primaryForeground,
        border: p.primary,
        hot: {bg: p.primaryInk, fg: p.primaryForeground, border: p.primaryInk},
        strong: true,
      };
    case 'destructive':
      return {
        bg: p.destructiveSoft,
        fg: p.destructiveInk,
        border: 'transparent',
        hot: {bg: p.destructive, fg: p.destructiveForeground, border: p.destructive},
        strong: true,
      };
    case 'outline':
    case 'neutral':
      return {
        bg: 'transparent',
        fg: p.foreground,
        border: p.borderStrong,
        hot: {bg: p.primarySofter, fg: p.primaryInk, border: p.primary},
      };
    case 'secondary':
    case 'soft':
      return {
        bg: p.elevated,
        fg: p.foreground,
        border: p.border,
        hot: {bg: p.elevated, fg: p.foreground, border: p.borderStrong},
      };
    case 'ghost':
    case 'toolbar':
      return {
        bg: 'transparent',
        fg: p.textSecondary,
        border: 'transparent',
        hot: {bg: p.accent, fg: p.foreground, border: 'transparent'},
      };
    case 'chrome':
      return {
        bg: 'transparent',
        fg: p.chromeForeground,
        border: 'transparent',
        hot: {bg: p.chromeHover, fg: p.chromeForeground, border: 'transparent'},
      };
  }
}

/** Height, side padding, label size and glyph size, per rung of the scale. */
const SIZES: Record<ButtonSize, {height: number; padding: number; font: number; icon: number}> = {
  xs: {height: controls.xs, padding: 6, font: 12, icon: 14},
  sm: {height: controls.sm, padding: 10, font: 13, icon: 15},
  md: {height: controls.md, padding: 14, font: 13, icon: 16},
  lg: {height: controls.lg, padding: 16, font: 14, icon: 18},
};

/** The slop that brings a painted box up to a 44pt touch target. */
function slopFor(height: number, width?: number): {top: number; bottom: number; left: number; right: number} {
  const v = Math.max(0, Math.round((44 - height) / 2));
  const h = width != null ? Math.max(0, Math.round((44 - width) / 2)) : 4;
  return {top: v, bottom: v, left: h, right: h};
}

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: radii.sm,
    borderWidth: 1,
    overflow: 'hidden',
  },
  // Dimmed, not hidden: a disabled button must still say what it would do.
  disabled: {opacity: 0.45},
  // `inline-flex`: в колонке кнопка обнимает подпись, а не тянется на всю ширину.
  hug: {alignSelf: 'flex-start'},
  title: {fontFamily: fonts.sans.regular},
  titleStrong: {fontFamily: fonts.sans.semibold},
  spinner: {marginRight: 2},
});

export type AppButtonProps = {
  title: string;
  onPress?: () => void;
  tone?: ButtonTone;
  size?: ButtonSize;
  /** A glyph before the title — plus for "add", send for "invite". */
  icon?: IconName;
  loading?: boolean;
  disabled?: boolean;
  /** Fully rounded. For floating and pill-shaped controls. */
  pill?: boolean;
  /** Stretch to the width of the container — a form's submit. */
  block?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export function AppButton({
  title,
  onPress,
  tone = 'primary',
  size = 'lg',
  icon,
  loading = false,
  disabled = false,
  pill = false,
  block = false,
  style,
  testID,
}: AppButtonProps) {
  const p = useTheme();
  const s = skin(p, tone);
  const dim = SIZES[size];
  // An in-flight action must not be fired twice — loading implies disabled.
  const inert = disabled || loading;
  // Обнимать подпись или растягиваться — решает контекст (см. `button-layout`).
  const hug = useButtonLayout() === 'hug' && !block;

  /* A pointer gets a fill change and that is enough feedback; a thumb covering the
     control cannot see the fill it is covering, so the press also scales. Native
     driver, same physics as PressCard. */
  const scale = useRef(new Animated.Value(1)).current;
  const spring = (to: number) =>
    Animated.spring(scale, {toValue: to, useNativeDriver: true, speed: 40, bounciness: 0}).start();

  return (
    <Animated.View
      style={[
        {transform: [{scale}]},
        block && {alignSelf: 'stretch'},
        hug && styles.hug,
        style,
      ]}>
      <Pressable
        testID={testID}
        onPress={inert ? undefined : onPress}
        onPressIn={() => !inert && spring(0.98)}
        onPressOut={() => !inert && spring(1)}
        disabled={inert}
        hitSlop={slopFor(dim.height)}
        accessibilityRole="button"
        accessibilityState={{disabled: inert, busy: loading}}
        accessibilityLabel={title}
        style={state => {
          const hot = isHot(state) && !inert;
          return [
            styles.base,
            {
              minHeight: dim.height,
              paddingHorizontal: dim.padding,
              borderRadius: pill ? radii.pill : radii.sm,
              backgroundColor: hot ? s.hot.bg : s.bg,
              borderColor: hot ? s.hot.border : s.border,
            },
            inert && styles.disabled,
          ];
        }}>
        {state => {
          const hot = isHot(state) && !inert;
          const fg = hot ? s.hot.fg : s.fg;
          return (
            <>
              {loading ? (
                <View style={styles.spinner}>
                  <ActivityIndicator testID="button-spinner" size="small" color={fg} />
                </View>
              ) : icon ? (
                <Icon name={icon} size={dim.icon} color={fg} />
              ) : null}
              <AppText
                color={fg}
                style={[s.strong ? styles.titleStrong : styles.title, {fontSize: dim.font}]}
                numberOfLines={1}>
                {title}
              </AppText>
            </>
          );
        }}
      </Pressable>
    </Animated.View>
  );
}

/** True while the pointer is over the control, or a finger is down on it. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/**
 * A square icon-only button — ui-kit `isIconOnly`: the compact affordance a header,
 * a panel head or a card corner needs (close, refresh, edit). Separate from AppButton
 * because it must stay square and because it takes an accessibility label instead of a
 * visible title.
 *
 * The same `hitSlop` rule applies, so a 24pt glyph button is still a 44pt target.
 */
export function IconButton({
  icon,
  onPress,
  label,
  tone = 'ghost',
  size = controls.md,
  disabled = false,
  testID,
  style,
}: {
  icon: IconName;
  onPress: () => void;
  /** Required: an icon with no label is invisible to a screen reader. */
  label: string;
  tone?: ButtonTone;
  size?: number;
  disabled?: boolean;
  testID?: string;
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  const s = skin(p, tone);
  return (
    <Pressable
      testID={testID}
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{disabled}}
      hitSlop={slopFor(size, size)}
      style={state => {
        const hot = isHot(state) && !disabled;
        return [
          {
            width: size,
            height: size,
            borderRadius: radii.sm,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: hot ? s.hot.bg : s.bg,
            borderWidth: 1,
            borderColor: hot ? s.hot.border : s.border,
          },
          disabled && styles.disabled,
          style,
        ];
      }}>
      {state => (
        <Icon
          name={icon}
          size={Math.round(size * 0.5)}
          color={isHot(state) && !disabled ? s.hot.fg : s.fg}
        />
      )}
    </Pressable>
  );
}
